<article
  class="daisy-card daisy-card-bordered bg-base-100 text-base-content shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer overflow-hidden h-full flex flex-col border-base-300 group"
  (click)="onCardClick()"
>
  <!-- Course Banner Image -->
  <figure
    class="relative aspect-video w-full m-0 bg-base-200 overflow-hidden shrink-0"
  >
    @if (bannerImage && !isImageError) {
      <img
        [src]="bannerImage"
        [alt]="course.title || 'Course Image'"
        class="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
        (error)="onImageError()"
      />
    } @else {
      <div
        class="absolute inset-0 flex flex-col items-center justify-center gap-2 text-base-content/50 bg-base-200"
      >
        <i class="fa-solid fa-graduation-cap text-3xl"></i>
        <span class="text-xs font-bold tracking-wider">{{
          getInitials(course.title)
        }}</span>
      </div>
    }

    <!-- Subject / Category Badge -->
    @if (subject || categoryName) {
      <span
        class="absolute top-3 left-3 daisy-badge daisy-badge-primary font-bold text-[0.65rem] uppercase tracking-wider shadow-sm max-w-[55%] truncate"
      >
        {{ subject || categoryName }}
      </span>
    }

    <!-- Status Badges -->
    @if (pendingStatus; as badge) {
      <span
        class="absolute top-3 right-3 daisy-badge {{ badge.badgeClass }} font-bold text-[0.65rem] uppercase tracking-wider shadow-sm flex items-center gap-1 shrink-0"
      >
        <i class="fa-solid {{ badge.icon }} text-[10px]"></i> {{ badge.label }}
      </span>
    } @else if (isEnrolled) {
      <span
        class="absolute top-3 right-3 daisy-badge daisy-badge-success text-white font-bold text-[0.65rem] uppercase tracking-wider shadow-sm flex items-center gap-1 shrink-0"
      >
        <i class="fa-solid fa-circle-check text-[10px]"></i> Enrolled
      </span>
    }
  </figure>

  <!-- Card Body -->
  <section
    class="daisy-card-body p-4 sm:p-5 flex-1 flex flex-col justify-between gap-3"
  >
    <div class="flex flex-col gap-2">
      <!-- Target Audience, Medium & Format Header -->
      <div
        class="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs font-semibold text-base-content/60 uppercase tracking-wider"
      >
        <span>{{ targetAudience || 'All Levels' }}</span>
        <span>•</span>
        <span>{{ mediumFormatted }}</span>
        <span>•</span>
        <span>Self-Paced</span>
      </div>

      <!-- Title -->
      <h3
        class="font-bold text-base sm:text-lg text-base-content line-clamp-2 leading-snug m-0 group-hover:text-primary transition-colors"
      >
        {{ course.title }}
      </h3>

      <!-- Short Description -->
      <p
        class="text-xs sm:text-sm text-base-content/70 line-clamp-2 mt-0.5 leading-relaxed m-0 min-h-[2.5rem]"
      >
        {{
          course.details.descriptionSummary ||
            course.details.description ||
            'No description provided.'
        }}
      </p>

      <!-- Course Highlights / Meta Chips -->
      <div
        class="flex flex-wrap items-center gap-3 pt-2 text-xs text-base-content/70 border-t border-base-200 mt-1"
      >
        @if (totalModules > 0) {
          <span
            class="inline-flex items-center gap-1.5"
            title="Curriculum Modules"
          >
            <i class="fa-solid fa-layer-group text-primary text-xs"></i>
            <span
              >{{ totalModules }}
              {{ totalModules === 1 ? 'Module' : 'Modules' }}</span
            >
          </span>
        }
        @if (totalLessons > 0) {
          <span
            class="inline-flex items-center gap-1.5"
            title="Lessons & Materials"
          >
            <i class="fa-solid fa-circle-play text-primary text-xs"></i>
            <span
              >{{ totalLessons }}
              {{ totalLessons === 1 ? 'Lesson' : 'Lessons' }}</span
            >
          </span>
        }
        <span
          class="inline-flex items-center gap-1.5"
          title="Estimated Duration"
        >
          <i class="fa-solid fa-clock text-primary text-xs"></i>
          <span>{{ durationLabel }}</span>
        </span>
      </div>
    </div>

    <!-- Bottom Footer: Price & CTA -->
    <div class="mt-auto pt-3 border-t border-base-200">
      @if (isOverdue) {
        <button
          type="button"
          class="daisy-btn daisy-btn-error daisy-btn-sm w-full gap-2 text-white font-semibold"
          (click)="onActionClick($event)"
        >
          <i class="fa-solid fa-credit-card text-xs"></i>
          <span>Pay Overdue Installment</span>
        </button>
      } @else if (isEnrolled) {
        <button
          type="button"
          class="daisy-btn daisy-btn-primary daisy-btn-sm w-full gap-2"
          (click)="onActionClick($event)"
        >
          <span>Continue Learning</span>
          <i class="fa-solid fa-arrow-right text-xs"></i>
        </button>
      } @else if (isPendingApproval) {
        <button
          type="button"
          class="daisy-btn daisy-btn-warning daisy-btn-outline daisy-btn-sm w-full gap-2"
          (click)="onActionClick($event)"
        >
          <i class="fa-solid fa-clock text-xs"></i>
          <span>Pending Approval — View Orders</span>
        </button>
      } @else if (isPaymentPending) {
        <button
          type="button"
          class="daisy-btn daisy-btn-warning daisy-btn-sm w-full gap-2"
          (click)="onActionClick($event)"
        >
          <i class="fa-solid fa-credit-card text-xs"></i>
          <span>Complete Payment</span>
        </button>
      } @else {
        <div class="flex items-center justify-between gap-2">
          <div class="flex flex-col">
            <span
              class="text-[0.65rem] font-bold uppercase tracking-wider text-base-content/60"
            >
              Tuition
            </span>
            <span
              class="text-base sm:text-lg font-extrabold text-primary leading-tight"
            >
              {{ priceFormatted }}
            </span>
          </div>
          <button
            type="button"
            class="daisy-btn daisy-btn-primary daisy-btn-sm"
            (click)="onActionClick($event)"
          >
            <span>Details</span>
            <i class="fa-solid fa-chevron-right ml-1 text-xs"></i>
          </button>
        </div>
      }
    </div>
  </section>
</article>
